<script setup lang="ts">
import {
  DrawerContent,
  DrawerDescription,
  DrawerHandle,
  DrawerOverlay,
  DrawerPortal,
  DrawerRoot,
  DrawerTitle,
} from 'vaul-vue'
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue'
// StemneConfirmModal is itself built on StemneModal, so this is a
// cyclic import. It's safe: neither module touches the other at
// module-eval time (only at render), and ESM live bindings have both
// default exports resolved by the time either renders. The prompt is
// rendered behind `v-if`, so the inner StemneModal never even mounts
// until a guarded dismissal fires.
import StemneConfirmModal from '@/components/ui/StemneConfirmModal.vue'
import { useEscapeStack } from '@/composables/useEscapeStack'

// Generic modal primitive with two responsive personalities:
//   - Desktop (>640px): centered card with our existing scale-in
//     animation. Built on Vue's <Transition> + Teleport.
//   - Mobile  (≤640px): bottom drawer powered by vaul-vue (the Vue
//     port of Emil Kowalski's Vaul). Brings drag-tied transforms,
//     velocity-based dismiss, rubber-band overdrag, and the
//     `shouldScaleBackground` page-scale effect — all the physics
//     we tried and failed to hand-roll. The page wrapper opted in
//     to scaling via `data-vaul-drawer-wrapper` in App.vue.
//
// The public API (props, slots, events) is unchanged so all callers
// keep working without edits.
const props = withDefaults(
  defineProps<{
    show: boolean
    title: string
    /** Short purpose/explanation announced with the dialog title. */
    description?: string
    width?: 'sm' | 'md' | 'lg' | 'xl'
    tone?: 'neutral' | 'destructive'
    showCloseButton?: boolean
    // Mono uppercase eyebrow rendered above the title, e.g. "ny booking"
    // or "møte · ma 18. mai 10:00". Optional — older callers don't pass it.
    kicker?: string
    // CSS length that locks the body height (applied as both min and max)
    // so the footer / primary CTA never moves when active-tab content
    // changes or when the modal is in mobile bottom-sheet mode. Tall
    // content scrolls inside; short content gets whitespace below.
    //
    // Pick a value ≥ the tallest tab/state. Wrap in a viewport clamp
    // (e.g. `min(480px, 60vh)`) so the lock auto-degrades on small
    // phones — that's important: a raw 480px on a 700pt-tall device
    // would push the card off the top of the viewport.
    //
    // Leave unset for non-tabbed modals where natural content height
    // is fine.
    bodyHeight?: string
    // When true, ESC and overlay-clicks are ignored. For irreversible
    // operations in flight (e.g. delete spinning), the consumer can lock
    // dismissal until the network settles.
    locked?: boolean
    // When false, clicking the overlay does NOT dismiss. ESC and the
    // close X still work. Use this for multi-step wizards where an
    // accidental click would lose user input. Mobile drawer's
    // swipe-down gesture is unaffected — that's an intentional motion,
    // not a stray click.
    dismissOnOverlay?: boolean
    // When true, EVERY dismissal path (overlay click, ESC, the close X,
    // and the mobile drawer's swipe-down) is intercepted: instead of
    // emitting `close`, the modal shows a "you have unsaved changes"
    // confirm prompt. `close` only fires once the operator confirms the
    // discard. Bind this to a `hasUnsavedChanges` computed (see the
    // useUnsavedChanges composable) so a stray click can't silently lose
    // edits. `locked` still wins — a locked modal ignores dismissal
    // entirely and never reaches the prompt.
    confirmOnClose?: boolean
    // Tighter header for dense multi-step wizards: 18px title (down from
    // --fs-h3) pulled closer to the kicker, so the chrome cedes vertical
    // room to the step content. Design revision of "Bekreft og send".
    compactHeader?: boolean
  }>(),
  {
    width: 'md',
    tone: 'neutral',
    showCloseButton: true,
    kicker: undefined,
    bodyHeight: undefined,
    locked: false,
    dismissOnOverlay: true,
    confirmOnClose: false,
    compactHeader: false,
  },
)

// `open` fires when the modal becomes visible — consumers use it to
// trigger fetches that should run on each open (e.g. revision history).
// `after-close` fires once the leave transition finishes; cleaner than
// watching show=false because the DOM is fully gone when it fires.
const emit = defineEmits<{
  close: []
  open: []
  'after-close': []
}>()

const cardRef = useTemplateRef<HTMLElement>('cardRef')
const titleId = ref(`modal-title-${Math.random().toString(36).slice(2, 8)}`)
const descriptionId = ref(`modal-description-${Math.random().toString(36).slice(2, 8)}`)

// ---------------------------------------------------------------
// Scroll affordance — when the body has more content below the fold,
// the footer grows a hairline + soft upward shadow so the action row
// doesn't read as a "false bottom". The hint clears once scrolled to
// the end. Footerless modals get the hint on the body's bottom edge.
// ---------------------------------------------------------------
const bodyRef = useTemplateRef<HTMLElement>('bodyRef')
const bodyContentRef = useTemplateRef<HTMLElement>('bodyContentRef')
const bodyHasMoreBelow = ref(false)

function updateScrollAffordance() {
  const el = bodyRef.value
  if (!el) {
    bodyHasMoreBelow.value = false
    return
  }
  const scrollable = el.scrollHeight - el.clientHeight > 1
  const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1
  bodyHasMoreBelow.value = scrollable && !atBottom
}

let bodyResizeObserver: ResizeObserver | null = null
let observedBody: HTMLElement | null = null

function teardownScrollWatch() {
  bodyResizeObserver?.disconnect()
  bodyResizeObserver = null
  observedBody?.removeEventListener('scroll', updateScrollAffordance)
  observedBody = null
}

function setupScrollWatch(el: HTMLElement | null) {
  teardownScrollWatch()
  if (!el) {
    bodyHasMoreBelow.value = false
    return
  }
  observedBody = el
  el.addEventListener('scroll', updateScrollAffordance, { passive: true })
  // jsdom (tests) ships no ResizeObserver — the scroll listener + open
  // recompute still cover the common path; guard so mount doesn't throw.
  if (typeof ResizeObserver !== 'undefined') {
    bodyResizeObserver = new ResizeObserver(() => updateScrollAffordance())
    bodyResizeObserver.observe(el)
    // Observe the content wrapper too: tabbed modals lock the body height,
    // so only the content's scrollHeight changes when the active tab swaps
    // — the scroll container itself never resizes and wouldn't fire.
    if (bodyContentRef.value) bodyResizeObserver.observe(bodyContentRef.value)
  }
  updateScrollAffordance()
}

// Bind when the body element mounts (modal opens) and tear down when it
// unmounts. Only one responsive branch renders at a time, so a single
// ref pair tracks whichever body is live.
watch(bodyRef, (el) => setupScrollWatch(el))

// True while the unsaved-changes confirm prompt is on screen. Every
// dismissal path funnels through tryClose, so guarding here covers the
// overlay click, ESC, the X, and the mobile swipe-down in one place.
const showDiscardPrompt = ref(false)

function tryClose() {
  if (props.locked) return
  if (props.confirmOnClose) {
    showDiscardPrompt.value = true
    return
  }
  emit('close')
}

// Operator confirmed the discard — drop the prompt and let the real
// close through.
function confirmDiscard() {
  showDiscardPrompt.value = false
  emit('close')
}

// Operator backed out — keep the modal (and its edits) open. On the
// mobile drawer a swipe-down had visually started to dismiss; because we
// never emitted `close`, `show` stayed true and vaul snaps the sheet back.
function cancelDiscard() {
  showDiscardPrompt.value = false
}

// Only the overlay-click path runs through this; ESC and the X
// stay routed via tryClose so they always work (subject to locked).
function tryDismissOverlay() {
  if (!props.dismissOnOverlay) return
  tryClose()
}

// Desktop overlay dismissal: a plain `@click.self` is not enough. A click
// event's target is resolved from the mouseUP location — but when mousedown
// and mouseup land on DIFFERENT elements (e.g. a text-selection drag that
// starts inside the card and is released on the backdrop), browsers retarget
// the click to the nearest common ancestor, which is the overlay itself.
// `.self` then passes and the modal closes on a drag it should have
// ignored. Tracking whether the preceding mousedown started INSIDE the
// card (rather than on the overlay itself) and skipping the dismiss in
// that case fixes it — a mousedown that starts on the overlay, or a
// synthetic `.click()` with no mousedown at all, still closes as before.
let dragStartedInsideCard = false
function onOverlayMouseDown(e: MouseEvent) {
  dragStartedInsideCard = e.target !== e.currentTarget
}
function onOverlayClick() {
  const blocked = dragStartedInsideCard
  dragStartedInsideCard = false
  if (blocked) return
  tryDismissOverlay()
}

// ---------------------------------------------------------------
// Responsive branch — vaul-vue's drawer on phones, our centered
// card on tablet/desktop. Reactive to viewport changes mid-session
// (e.g. window resize, rotation).
// ---------------------------------------------------------------
const isDrawer = ref(false)
let mq: MediaQueryList | null = null
function onBreakpointChange(e: MediaQueryListEvent) {
  isDrawer.value = e.matches
}

// ---------------------------------------------------------------
// Vaul events → our emits. vaul-vue manages inert / scroll-lock /
// focus-trap / ESC internally; we just bridge open & after-close.
// ---------------------------------------------------------------
function onDrawerOpenChange(open: boolean) {
  // vaul-vue re-emits `update:open(false)` in TWO cases: a genuine user
  // dismissal (swipe-down/ESC/overlay it owns), AND when the parent drives
  // the `open` prop to false — a programmatic close, e.g. right after a
  // successful save (the 200 OK → parent sets `show = false`). Only the
  // first is a dismissal the operator initiated; routing the second through
  // tryClose pops the "Ulagrede endringer" prompt on a modal the parent
  // already chose to close, with edits that were in fact just saved.
  //
  // Tell them apart by `props.show`: a user dismissal fires while it's still
  // true (our close only leaves via emit('close'), so the parent hasn't
  // flipped `show` yet), whereas a programmatic close has already set it
  // false before vaul reacts. The desktop branch never had this bug — its
  // `show` watcher clears the prompt on close — so this realigns mobile.
  if (!open && props.show) tryClose()
}
function onDrawerAnimationEnd(open: boolean) {
  if (!open) emit('after-close')
}

// Watch `show` so we emit `open` regardless of which branch is rendering.
// Using a watcher (vs. firing inside Vue's <Transition> @enter) means
// the event timing is identical on mobile (where vaul-vue runs) and
// desktop (where our <Transition> runs).
watch(
  () => props.show,
  (now) => {
    if (now) emit('open')
    // If the modal is closed by any means (incl. a parent closing it
    // programmatically), drop a lingering discard prompt so it doesn't
    // resurface on the next open.
    else showDiscardPrompt.value = false
  },
)

// ---------------------------------------------------------------
// Desktop-only: keyboard ESC and inert/scroll lifecycle. On mobile
// vaul-vue covers all of this; we'd be doubling up.
//
// Escape is routed through the shared overlay stack (#2330 / #2331):
// this modal registers itself while open, and only reacts to Escape
// when it's the topmost (most-recently-opened) overlay — a sibling
// StemneConfirmModal opened on top of it, or a StemneSelect dropdown
// open inside it, take priority instead of both firing on one keypress.
// ---------------------------------------------------------------
const isDesktopOpen = computed(() => props.show && !isDrawer.value)
const handleEscapeKeydown = useEscapeStack(isDesktopOpen, () => tryClose())

function onKeydown(e: KeyboardEvent) {
  handleEscapeKeydown(e)
}

let preOpenFocus: HTMLElement | null = null
let prevBodyOverflow = ''
let openLockApplied = false

function onTransitionEnter() {
  preOpenFocus = document.activeElement as HTMLElement | null
  document.getElementById('app')?.setAttribute('inert', '')
  prevBodyOverflow = document.body.style.overflow
  document.body.style.overflow = 'hidden'
  openLockApplied = true
  requestAnimationFrame(() => cardRef.value?.focus())
}

// Undo what onTransitionEnter did. Idempotent, and a no-op for a modal that
// never opened — otherwise a closed sibling unmounting would strip the inert
// an open one just set.
function releaseOpenLock() {
  if (!openLockApplied) return
  openLockApplied = false
  document.getElementById('app')?.removeAttribute('inert')
  document.body.style.overflow = prevBodyOverflow
}

function onTransitionAfterLeave() {
  releaseOpenLock()
  if (preOpenFocus?.isConnected) preOpenFocus.focus()
  preOpenFocus = null
  emit('after-close')
}

onMounted(() => {
  document.addEventListener('keydown', onKeydown)
  // jsdom (used in unit tests) doesn't ship matchMedia. Guarding lets
  // the component mount in tests without a global polyfill — tests
  // exercise the desktop branch by default since isDrawer stays false.
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    mq = window.matchMedia('(max-width: 640px)')
    isDrawer.value = mq.matches
    mq.addEventListener('change', onBreakpointChange)
  }
})
onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown)
  mq?.removeEventListener('change', onBreakpointChange)
  teardownScrollWatch()
  // A route change with the modal still open (an API gate redirecting
  // mid-save, a link inside the modal) destroys this subtree, so the
  // Transition's after-leave never runs. Without releasing here, #app keeps
  // `inert` and the WHOLE app silently stops accepting clicks on the page we
  // just navigated to — with nothing on screen to explain it.
  releaseOpenLock()
})

const cardClass = computed(() => [
  'modal-card',
  `modal-card--${props.width}`,
  `modal-card--${props.tone}`,
])
</script>

<template>
  <!-- Mobile (≤640px): vaul-vue drawer. Drag mechanics, page-scale,
       focus trap, ESC, scroll-lock, inert — all delegated. -->
  <DrawerRoot
    v-if="isDrawer"
    :open="show"
    :should-scale-background="true"
    :dismissible="!locked"
    :modal="true"
    :handle-only="true"
    @update:open="onDrawerOpenChange"
    @animation-end="onDrawerAnimationEnd"
  >
    <DrawerPortal>
      <DrawerOverlay class="stemne-drawer__overlay" />
      <DrawerContent
        class="stemne-drawer__content"
        :class="`stemne-drawer__content--${props.tone}`"
      >
        <!-- Custom header slot: caller owns the full header layout and may
             place interactive controls in it, so we keep only the thin grab
             bar (not the full-header drag affordance below). vaul-vue still
             needs a DrawerTitle for screen readers, so we render a
             visually-hidden one alongside the slot. -->
        <template v-if="$slots.header">
          <DrawerHandle class="stemne-drawer__handle" />
          <DrawerTitle as="span" class="sr-only">{{ title }}</DrawerTitle>
          <DrawerDescription v-if="description" as="span" class="sr-only">
            {{ description }}
          </DrawerDescription>
          <div class="modal-header modal-header--custom" data-vaul-no-drag @pointerdown.stop>
            <slot name="header" />
          </div>
        </template>

        <!-- Default header: the whole header strip is a drag affordance
             (option B — enlarge the grab target so a downward drag can start
             from anywhere on the title area, not just the 40px bar). The
             DrawerHandle overlays the strip and is raised above the
             (non-interactive) title/kicker; the close button is raised above
             the overlay so its tap still lands. While `locked`, the overlay
             is inert: vaul's handle-click calls closeDrawer() directly,
             bypassing `dismissible`, so an enlarged tap target would
             otherwise let an in-flight operation be tap-dismissed. -->
        <div v-else class="stemne-drawer__draghead">
          <DrawerHandle
            class="stemne-drawer__draghandle"
            :class="{ 'stemne-drawer__draghandle--locked': locked }"
          />
          <header class="modal-header" :class="{ 'modal-header--compact': compactHeader }">
            <div class="modal-header__text">
              <div v-if="kicker" class="modal-kicker kicker">
                {{ kicker }}
              </div>
              <DrawerTitle as="h2" class="modal-title">
                {{ title }}
              </DrawerTitle>
              <!-- vaul wants a description; an explicit one wins, and the
                   kicker is the fallback it used to be. Without this, a
                   drawer with a kicker and no description registers none at
                   all — which is every kicker-only drawer in the app. -->
              <DrawerDescription v-if="description || kicker" as="span" class="sr-only">
                {{ description || kicker }}
              </DrawerDescription>
            </div>
            <!-- Close button carries the same three anti-drag defences as the
                 body/footer below (data-vaul-no-drag + @pointerdown.stop +
                 @click.stop): the button sits inside the full-strip drag
                 handle, and on iOS touch vaul pointer-captures the tap so its
                 @click never fires. z-index alone only fixes the mouse path. -->
            <button
              v-if="showCloseButton"
              type="button"
              class="modal-close"
              :aria-label="$t('common.close')"
              :disabled="locked"
              data-vaul-no-drag
              @pointerdown.stop
              @click.stop="tryClose"
            >
              ×
            </button>
          </header>
        </div>

        <!--
          Four independent defences against vaul/reka-ui dismissing
          the drawer on a tap inside content. Each was found the hard
          way (see memory: stemne_vaul_button_drag_misread,
          feedback_vaul_modal_body_pointer_events) and each blocks a
          different path that, alone, was enough to fire close:

            1. handleOnly on DrawerRoot, gates vaul's drag tracking
               so DrawerContent ignores pointerdown unless on Handle.
            2. data-vaul-no-drag, second-layer skip for vaul's
               release-path drag math.
            3. @pointerdown.stop, halts the bubble before reka-ui's
               document pointerdown listener runs. Without this, the
               DismissableLayer's outside check fired anyway (likely
               a layer-ancestry edge case) and tripped emitOpenChange
               (false), closing the drawer right after the button's
               @click handler ran.
            4. @click.stop, the TOUCH counterpart to #3. reka-ui's
               usePointerDownOutside does NOT dismiss on pointerdown for
               `pointerType === 'touch'`; it defers the dismiss to the
               next document-level `click` (a ~350ms tap-delay guard).
               #3 only stops the pointerdown phase, so once a teleported
               popup inside the sheet (customer combobox, service select,
               date picker — all portalled OUT of this subtree, so their
               taps reach the document handler and arm the one-shot click
               listener) primes that deferred dismiss, the very next tap
               that bubbles a click to document — "Neste" — fired it,
               popping the unsaved-changes guard on iOS. Stopping the
               click here keeps in-sheet taps from ever reaching that
               document listener.

          The button's own @click runs first (target before the body/
          footer's .stop during bubbling), so handlers inside the drawer
          still fire on both #3 and #4. The DrawerHandle above is left
          untouched so swipe-down to dismiss still works from the handle
          bar only.
        -->
        <div
          ref="bodyRef"
          class="modal-body"
          :class="{
            'modal-body--locked': !!bodyHeight,
            'modal-body--has-more': bodyHasMoreBelow && !$slots.footer,
          }"
          :style="bodyHeight ? { minHeight: bodyHeight, maxHeight: bodyHeight } : undefined"
          data-vaul-no-drag
          @pointerdown.stop
          @click.stop
        >
          <div ref="bodyContentRef" class="modal-body__content">
            <slot />
          </div>
        </div>

        <footer
          v-if="$slots.footer"
          class="modal-footer"
          :class="{ 'modal-footer--has-more': bodyHasMoreBelow }"
          data-vaul-no-drag
          @pointerdown.stop
          @click.stop
        >
          <slot name="footer" />
        </footer>
      </DrawerContent>
    </DrawerPortal>
  </DrawerRoot>

  <!-- Desktop (>640px): centered card with our scale-in motion. -->
  <Teleport v-else to="body">
    <Transition
      name="modal"
      @enter="onTransitionEnter"
      @after-leave="onTransitionAfterLeave"
    >
      <div
        v-if="show"
        class="modal-overlay"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        :aria-describedby="description ? descriptionId : undefined"
        @mousedown="onOverlayMouseDown"
        @click.self="onOverlayClick"
      >
        <article
          v-focus-trap
          ref="cardRef"
          :class="cardClass"
          tabindex="-1"
        >
          <!-- Custom header slot: caller owns the full header layout.
               aria-labelledby still points at titleId, so we render a
               visually-hidden title to keep the dialog name announced. -->
          <template v-if="$slots.header">
            <h2 :id="titleId" class="sr-only">{{ title }}</h2>
            <div class="modal-header modal-header--custom">
              <slot name="header" />
            </div>
          </template>
          <header
            v-else
            class="modal-header"
            :class="{ 'modal-header--compact': compactHeader }"
          >
            <div class="modal-header__text">
              <div v-if="kicker" class="modal-kicker kicker">{{ kicker }}</div>
              <h2 :id="titleId" class="modal-title">{{ title }}</h2>
            </div>
            <button
              v-if="showCloseButton"
              type="button"
              class="modal-close"
              :aria-label="$t('common.close')"
              :disabled="locked"
              @click="tryClose"
            >
              ×
            </button>
          </header>

          <p v-if="description" :id="descriptionId" class="sr-only">{{ description }}</p>

          <div
            ref="bodyRef"
            class="modal-body"
            :class="{
              'modal-body--locked': !!bodyHeight,
              'modal-body--has-more': bodyHasMoreBelow && !$slots.footer,
            }"
            :style="bodyHeight ? { minHeight: bodyHeight, maxHeight: bodyHeight } : undefined"
          >
            <div ref="bodyContentRef" class="modal-body__content">
              <slot />
            </div>
          </div>

          <footer
            v-if="$slots.footer"
            class="modal-footer"
            :class="{ 'modal-footer--has-more': bodyHasMoreBelow }"
          >
            <slot name="footer" />
          </footer>
        </article>
      </div>
    </Transition>
  </Teleport>

  <!-- Unsaved-changes guard. Rendered as a sibling (not nested in the
       card/drawer) so it teleports to body in its own layer and stacks
       above the modal it guards. Only mounts while the prompt is up. -->
  <StemneConfirmModal
    v-if="showDiscardPrompt"
    :show="showDiscardPrompt"
    :title="$t('uiStemneModal.discardTitle')"
    :body="$t('uiStemneModal.discardBody')"
    :confirm-label="$t('uiStemneModal.discardConfirm')"
    :cancel-label="$t('uiStemneModal.discardCancel')"
    variant="destructive"
    @confirm="confirmDiscard"
    @cancel="cancelDiscard"
  />
</template>

<style lang="scss" scoped>
// ---------------------------------------------------------------
// Mobile drawer (vaul-vue). Vaul handles motion; we only style.
// ---------------------------------------------------------------

.stemne-drawer__overlay {
  position: fixed;
  inset: 0;
  background: rgba(28, 25, 21, 0.5);
  z-index: 60;
}

.stemne-drawer__content {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 61;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  background: var(--bg-card);
  border-radius: var(--radius-sheet) var(--radius-sheet) 0 0;
  // iOS sheets give a touch more vertical padding above the handle
  // and below the footer; mirror that here. The handle itself eats
  // some of the top space.
  padding: var(--space-3) var(--space-5) var(--space-5);
  // The footer reads this to bleed its divider out to the sheet edges.
  --modal-px: var(--space-5);
  max-height: 88vh;
  // Outline keeps focus indication visible without doubling our card
  // shadow during the open animation.
  outline: none;

  &--destructive {
    border-left: 3px solid var(--fg-destructive);
  }
}

.stemne-drawer__handle {
  // vaul-vue's DrawerHandle ships with default styles; override for
  // ours. The element is button-like and supports keyboard cycling
  // through snap points (we don't use snap points yet, but the
  // primitive is here when we do).
  width: 40px;
  height: 4px;
  border-radius: 2px;
  background: var(--border-stronger);
  margin: 0 auto var(--space-2);
  // Block the default vaul styles in case they sneak through.
  cursor: grab;
  &:active { cursor: grabbing; }
}

// ---------------------------------------------------------------
// Default-header drag affordance (option B). The whole header strip
// is draggable to dismiss, not just the thin bar. The DrawerHandle
// overlays the strip (inset: 0) and is raised above the title/kicker
// (non-interactive) so a press anywhere there starts a drag; the close
// button is raised above the overlay so its tap still lands.
// ---------------------------------------------------------------
.stemne-drawer__draghead {
  position: relative;
  // Room for the grabber bar above the title row.
  padding-top: var(--space-3);
}

.stemne-drawer__draghandle {
  position: absolute;
  inset: 0;
  z-index: 1;
  // Override vaul-vue's injected fixed-size bar ([data-vaul-handle] is a
  // 32×5 centered pill) — we want a full-strip overlay instead. The
  // visible grabber is drawn via ::before below.
  width: auto;
  height: auto;
  margin: 0;
  background: transparent;
  opacity: 1;
  cursor: grab;
  &:active { cursor: grabbing; }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 40px;
    height: 4px;
    border-radius: 2px;
    background: var(--border-stronger);
  }

  // Operation in flight: make the overlay inert so neither a drag nor
  // vaul's handle-click (which calls closeDrawer() directly, ignoring
  // `dismissible`) can dismiss the sheet.
  &--locked {
    pointer-events: none;
  }
}

.stemne-drawer__draghead .modal-close {
  position: relative;
  z-index: 2;
}

// ---------------------------------------------------------------
// Desktop centered card.
// ---------------------------------------------------------------

.modal-overlay {
  position: fixed;
  inset: 0;
  display: grid;
  place-items: center;
  padding: var(--space-4);
  background: transparent;
  z-index: 60;
  overflow-y: auto;

  // Dim layer on a pseudo-element so the card child does NOT inherit
  // a parent fade — that desync caused the "backdrop poofs in"
  // problem during the iOS-curve attempt.
  &::before {
    // bottom-nav-ok: z-index -1 is relative to .modal-overlay's own stacking
    // context (z-index 60), so this dim layer already sits above the mobile
    // tab bar — covering it is the point.
    content: '';
    position: fixed;
    inset: 0;
    background: rgba(28, 25, 21, 0.5);
    pointer-events: none;
    z-index: -1;
    transition: opacity 180ms var(--ease-sheet-in);
  }
}

.modal-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-5) var(--space-6);
  // The footer reads this to bleed its divider out to the card edges.
  --modal-px: var(--space-6);
  border-radius: var(--radius-sheet);
  background: var(--bg-card);
  box-shadow: 0 8px 24px rgba(28, 25, 21, 0.18);
  max-height: calc(100vh - 2 * var(--space-4));

  &--sm { width: min(440px, 100%); }
  &--md { width: min(560px, 100%); }
  &--lg { width: min(720px, 100%); }
  &--xl { width: min(940px, 100%); }

  &--destructive {
    border-left: 3px solid var(--fg-destructive);
  }

  &:focus { outline: none; }
}

// ---------------------------------------------------------------
// Header / body / footer — shared between mobile and desktop.
// ---------------------------------------------------------------

.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-3);
}

// Custom-header slot: the caller owns layout entirely (e.g. the
// centered icon + kicker + serif heading of the welcome modal). We
// only provide the slot wrapper; no flex row, no close button.
.modal-header--custom {
  display: block;
}

// Visually-hidden but screen-reader-available. Used to satisfy
// vaul-vue's DrawerTitle / the desktop dialog's aria-labelledby when
// the visible header is supplied via the custom slot.
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.modal-header__text {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.modal-title {
  font-family: var(--font-serif);
  font-size: var(--fs-h3);
  font-weight: var(--fw-medium);
  color: var(--fg-1);
  margin: 0;
}

// Compact wizard header: smaller title, tighter to the kicker.
.modal-header--compact {
  .modal-header__text {
    gap: 3px;
  }

  .modal-title {
    font-size: var(--fs-body-lg);
    letter-spacing: -0.015em;
    line-height: 1.15;
  }
}

.modal-close {
  background: transparent;
  border: none;
  font-size: var(--fs-h2);
  line-height: 1;
  color: var(--fg-muted);
  cursor: pointer;
  padding: 0 var(--space-2);

  &:hover:not(:disabled) { color: var(--fg-1); }
  &:focus-visible {
    outline: 1.5px solid var(--moss);
    outline-offset: 2px;
    border-radius: var(--radius-sm);
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

.modal-body {
  // overflow-y: auto clips both axes per CSS spec — children's
  // focus rings (1.5px outline + 2px offset = 3.5px) get cut off at
  // the body's edges otherwise. 4px of internal padding gives them
  // room; equal negative margin neutralises the layout shift.
  overflow-y: auto;
  padding: 4px;
  margin: -4px;
  // Slim, flat scrollbar in ink 20% — flat surface, no gradient, in
  // line with the design system. Replaces the OS default chrome bar.
  scrollbar-width: thin;
  scrollbar-color: rgba(28, 25, 21, 0.2) transparent;
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(28, 25, 21, 0.2);
    border-radius: 999px;
  }
  // Footerless modals: hint "more below" with a soft inset shadow on
  // the body's own bottom edge (inset shadows paint on the fixed
  // viewport, not the scrolling content, so it stays pinned).
  transition: box-shadow var(--duration-move) var(--ease-default);

  &--locked {
    flex-shrink: 0;
  }

  &--has-more {
    box-shadow: inset 0 -12px 10px -10px rgba(28, 25, 21, 0.16);
  }
}

// Holds the stacked slot content. Lives inside the scroll container so
// a ResizeObserver here catches content-height changes (tab swaps) even
// when the body height is locked.
.modal-body__content {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2);
  margin-top: var(--space-2);
  // Bleed the footer (and its divider) out to the card/sheet edges so
  // the hairline reads as a full-width separator rather than an inset
  // rule. The action row stays aligned to the content via the
  // re-applied horizontal padding. --modal-px is set per card variant.
  margin-inline: calc(-1 * var(--modal-px, var(--space-6)));
  padding: var(--space-3) var(--modal-px, var(--space-6)) 0;
  // Always-present transparent border so toggling the colour on does
  // not reflow the row.
  border-top: 1px solid transparent;
  transition:
    border-color var(--duration-move) var(--ease-default),
    box-shadow var(--duration-move) var(--ease-default);

  &--has-more {
    border-top-color: var(--border-subtle);
    // Soft upward shadow ("mykt skyggehint") so the scrolling content
    // peeks and fades under the footer instead of cutting clean.
    box-shadow: 0 -9px 12px -7px rgba(28, 25, 21, 0.11);
  }
}

// ---------------------------------------------------------------
// Desktop animation only — mobile motion is owned by vaul-vue.
// ---------------------------------------------------------------

.modal-enter-active,
.modal-leave-active {
  transition: none;
}

.modal-enter-from::before,
.modal-leave-to::before {
  opacity: 0;
}
.modal-leave-active::before {
  transition: opacity var(--duration-sheet-out) var(--ease-sheet-out);
}

.modal-enter-from .modal-card,
.modal-leave-to .modal-card {
  opacity: 0;
  transform: scale(0.96);
}
.modal-enter-active .modal-card {
  transition:
    opacity 180ms var(--ease-sheet-in),
    transform 180ms var(--ease-sheet-in);
}
.modal-leave-active .modal-card {
  transition:
    opacity 140ms var(--ease-sheet-out),
    transform 140ms var(--ease-sheet-out);
}

// ---------------------------------------------------------------
// Reduced motion — desktop branch only. vaul-vue handles RM on
// mobile via its Reka UI primitives.
// ---------------------------------------------------------------
@media (prefers-reduced-motion: reduce) {
  .modal-enter-active,
  .modal-leave-active,
  .modal-enter-active .modal-card,
  .modal-leave-active .modal-card,
  .modal-card,
  .modal-overlay::before,
  .modal-footer,
  .modal-body {
    transition: none !important;
    animation: none !important;
  }
  .modal-enter-from .modal-card,
  .modal-leave-to .modal-card {
    transform: none !important;
    opacity: 1 !important;
  }
  .modal-enter-from::before,
  .modal-leave-to::before {
    opacity: 1 !important;
  }
}
</style>
